import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import Collapse, { Panel } from 'components/Collapse';
import Header from 'views/pages/ClientDetail/CompanyOfficial/components/Header';
import NotFound from 'views/pages/ClientDetail/CompanyOfficial/components/NotFound';
import StatusTag from 'views/pages/ClientDetail/CompanyOfficial/components/StatusTag';
import PanelHeader from 'views/pages/ClientDetail/CompanyOfficial/PanelHeader';
import { ALL_OPTION } from 'views/pages/ClientDetail/CompanyOfficial/constants';
import { TooltipBlock } from 'components/Tooltip';
import {
  afterCbFn,
  isAlreadyNew,
  getLabel,
} from 'views/pages/ClientDetail/CompanyOfficial/utils';
import Section from './components/Section';
import { getTruckBR, getDriverTrailerAssigned } from './utils';
import { DEFAULT_SECTIONS, NEW_KEY } from './constants';
import {
  CollapseTable,
  CollapseAdditionalLabel,
  LabelWithAvatar,
} from '../components/Elements';
import { onKeyChangeFn, getEditKeys, editChangeFn, getIsKey } from '../utils';
import { useIndexData, useActions } from './selectorData';
import classes from './Truck.module.scss';

const Truck = ({
  className,
  confirmModal,
  openWarningModal,
  setIsEditing,
  activeTab,
  clientId,
  trucks_count,
  setIsEdit,
  status,
  setStatus,
  onUpdated,
  permission,
}) => {
  const setIsEditParent = setIsEdit;
  const { trucks, updateTruckLoading, vinDecoderLoading } = useIndexData();
  const {
    vinDecoder,
    getTrucks,
    updateTruck,
    createTruck,
    syncSetSectionTotal,
    updateTruckStatus,
  } = useActions();
  const [createLoading, setCreateLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState({});
  const [avatarFinalSrc, setAvatarFinalSrc] = useState('');
  const [finalFile, setFinalFile] = useState({});
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [activeKeys, setActiveKeys] = useState([]);
  const [editKeys, setEditKeys] = useState([]);
  const [editChanged, setEditChanged] = useState({});
  const notFound = sections.length === 0 && trucks.length === 0;
  const handleEditChange = (...params) =>
    editChangeFn(editChanged, setEditChanged, setIsEditing, ...params);
  const handleSubmit = (id, currentProfilePic) => (
    values,
    searchFields,
    cb
  ) => {
    const truckId = id && id !== NEW_KEY ? id : '';
    const actionFn = truckId ? updateTruck : createTruck;
    if (!truckId) {
      setCreateLoading(true);
    }
    actionFn(
      clientId,
      getTruckBR(
        values,
        truckId,
        finalFile,
        searchFields,
        currentProfilePic,
        avatarFinalSrc
      ),
      afterCbFn(
        res => {
          if (onUpdated) onUpdated();
          if (!truckId) {
            syncSetSectionTotal(trucks_count + 1, 'truck_info', 'trucks_count');
            handleLoadTruck(status, () => {
              setCreateLoading(false);
              if (cb) cb();
            });
          } else if (cb) cb();
        },
        () => {
          if (!truckId) setCreateLoading(false);
        }
      ),
      truckId
    );
  };
  const handleLoadTruck = (val, cb) => {
    const filters = {};
    if (val !== ALL_OPTION.value) {
      filters.status = val;
    }
    getTrucks({ id: clientId }, filters, afterCbFn(cb));
  };
  const handleSetStatusLoading = (id, val) => {
    setStatusLoading({
      ...statusLoading,
      [id]: val,
    });
  };
  const handleStatusChange = id => newStatus => {
    handleSetStatusLoading(id, true);
    updateTruckStatus(
      clientId,
      id,
      { status: newStatus },
      afterCbFn(
        () => {
          if (onUpdated) onUpdated();
          handleLoadTruck(status, () => {
            handleSetStatusLoading(id, false);
            setActiveKeys(activeKeys.filter(key => String(key) !== String(id)));
          });
        },
        () => handleSetStatusLoading(id, false)
      )
    );
  };

  useEffect(() => {
    if (activeTab === 1) {
      setSections(DEFAULT_SECTIONS);
      setEditKeys([]);
      setEditChanged({});
    }
  }, [activeTab]);
  useEffect(() => {
    setIsEditParent(sections.length > 0 || editKeys.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, editKeys]);

  return (
    <div className={cn(classes.wrapper, className)}>
      <Header
        title="Trucks"
        status={status}
        setStatus={val => {
          setStatus(val);
          handleLoadTruck(val);
        }}
        onAdd={
          !permission.addRecord
            ? null
            : () => {
                if (!isAlreadyNew(sections)) {
                  setSections([
                    { label: 'New Truck', id: NEW_KEY, isNew: true },
                    ...sections,
                  ]);
                  setEditKeys([...editKeys, NEW_KEY]);
                  setActiveKeys([...activeKeys, NEW_KEY]);
                  setFinalFile({});
                  setAvatarFinalSrc('');
                }
              }
        }
        className={classes.header}
      />
      {notFound ? (
        <NotFound />
      ) : (
        <div className={classes.content}>
          <CollapseTable hideHeader section className={classes.sectionTable}>
            <Collapse
              destroyInactivePanel
              onChange={keys =>
                onKeyChangeFn(keys, activeKeys, editKeys, setActiveKeys)
              }
              activeKey={activeKeys}
            >
              {[...sections, ...trucks].map(section => {
                const { isNew, id, truck_unit, make, color, year } = section;
                const key = String(id);
                const label = truck_unit || getLabel(section);
                const isActive = getIsKey(activeKeys, key);
                const isEdit = getIsKey(editKeys, key);
                const sectionInnerProps = {
                  clientId,
                  rootKey: key,
                  isEdit,
                  isActive,
                  confirmModal,
                  setIsEdit: edit => {
                    const newEditKeys = getEditKeys(editKeys, edit, key);
                    setEditKeys(newEditKeys);
                    if (!edit) {
                      handleEditChange(key, false, newEditKeys);
                    }
                    if (isNew && !edit) {
                      setSections(DEFAULT_SECTIONS);
                    }
                    setFinalFile({});
                    setAvatarFinalSrc(section.profile_pic);
                  },
                  data: section,
                  onFormChange: () => {
                    handleEditChange(key, true, editKeys);
                  },
                  openWarningModal,
                  loading: isNew ? createLoading : updateTruckLoading[id],
                  statusLoading: statusLoading[id],
                  onSubmit: handleSubmit(id, section.profile_pic),
                  onStatusChange: handleStatusChange(id),
                  vinDecoder,
                  vinDecoderLoading,
                  permission,
                };
                const avatarProps = {
                  confirmModal,
                  avatarFinalSrc,
                  setAvatarFinalSrc,
                  finalFile,
                  setFinalFile,
                  profilePic: section.profile_pic,
                };
                const { drivers, trailers } = getDriverTrailerAssigned(section);
                const driversDisplay = drivers.join(', ');
                const trailersDisplay = trailers.join(', ');
                const pHeaderProps = {
                  isEdit,
                  firstIsSmall: true,
                  label: (
                    <LabelWithAvatar
                      {...avatarProps}
                      label={label}
                      isEdit={isEdit}
                      onDelete={cb => {
                        setAvatarFinalSrc('');
                        if (cb) cb();
                      }}
                    />
                  ),
                  isActive,
                  className: cn(classes.panelHeader, {
                    [classes.isActive]: isActive,
                    [classes.isEdit]: isEdit,
                  }),
                  additionalLabel: (
                    <CollapseAdditionalLabel
                      isActive={isActive}
                      has3Items
                      className={classes.additionalLabel}
                    >
                      {!isNew && (
                        <>
                          <span>
                            {' '}
                            <span>
                              Driver:
                              <TooltipBlock tooltip={driversDisplay}>
                                {driversDisplay}
                              </TooltipBlock>
                            </span>
                            <span>
                              Trailers:
                              <TooltipBlock tooltip={trailersDisplay}>
                                {trailersDisplay}
                              </TooltipBlock>
                            </span>
                          </span>
                          <span>
                            <span>
                              Year:
                              <TooltipBlock tooltip={year}>{year}</TooltipBlock>
                            </span>
                            <span>
                              Make:
                              <TooltipBlock tooltip={make}>{make}</TooltipBlock>
                            </span>
                            <span>
                              Color:
                              <TooltipBlock tooltip={color}>
                                {color}
                              </TooltipBlock>
                            </span>
                          </span>
                        </>
                      )}
                    </CollapseAdditionalLabel>
                  ),
                };
                return (
                  <Panel
                    forceRender={true}
                    className={classes.panel}
                    header={<PanelHeader {...pHeaderProps} />}
                    key={key}
                    extra={
                      !isActive ? <StatusTag status={section.status} /> : null
                    }
                  >
                    <Section {...sectionInnerProps} />
                  </Panel>
                );
              })}
            </Collapse>
          </CollapseTable>
        </div>
      )}
    </div>
  );
};

export default Truck;
